import { t } from "@travily/i18n";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { ContentField, contentFieldCopy } from "@/components/ListingFields/ContentField";

const COLOR = "green" as const;

export interface ProductContentStepViewProps {
  /** Current stage, 1-indexed, for the progress bar. */
  currentStage: number;
  /** Total stages driving the progress bar. */
  totalStages: number;
  /** Back handler; omit on the first stage to hide the back affordance. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Save and finish later"). */
  topBarActions: ReactNode;
  /** Primary "Continue" handler. */
  onPrimary: () => void;
  /** Whether the form save is in flight; combined with `isUploading` for the button pending state. */
  primaryPending: boolean;
  /** Selected delivery mode. */
  deliveryKind: "file" | "link";
  /** Fires when the delivery mode changes (raw string from the radio group). */
  onKindChange: (value: string) => void;
  /** Name of the currently attached/picked file, if any. */
  fileName?: string;
  /** Size in bytes of the just-picked file, if any. */
  fileSize?: number;
  /** File field error message; when present the drop field renders invalid. */
  fileError?: string;
  /** Whether a direct upload is currently in progress. */
  isUploading: boolean;
  /** Upload progress, 0–100. */
  progress: number;
  /** Fires with the chosen `File` to begin the upload. */
  onFileSelect: (file: File) => void;
  /** Clears the currently attached file. */
  onFileRemove: () => void;
  /** Current delivery URL value for the link input. */
  deliveryUrl: string;
  /** Link field error message; when present the input renders invalid. */
  linkError?: string;
  /** Fires on link input change with the new value. */
  onDeliveryUrlChange: (value: string) => void;
}

export const ProductContentStepView = ({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  deliveryKind,
  onKindChange,
  fileName,
  fileSize,
  fileError,
  isUploading,
  progress,
  onFileSelect,
  onFileRemove,
  deliveryUrl,
  linkError,
  onDeliveryUrlChange,
}: ProductContentStepViewProps) => {
  const { heading, helper } = contentFieldCopy();

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending || isUploading}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage heading={heading} helper={helper}>
        <div className="w-full max-w-[600px]">
          <ContentField
            deliveryKind={deliveryKind}
            deliveryUrl={deliveryUrl}
            fileError={fileError}
            fileName={fileName}
            fileSize={fileSize}
            isUploading={isUploading}
            linkError={linkError}
            onDeliveryUrlChange={onDeliveryUrlChange}
            onFileRemove={onFileRemove}
            onFileSelect={onFileSelect}
            onKindChange={onKindChange}
            progress={progress}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
};
